import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/shared/utils/supabase'
import { Eye, EyeOff, AlertCircle } from '@/ui/icons'
import { Button, Input, Card, CardContent, CardHeader, CardTitle, CardDescription, WaveLoader } from '@/ui/components/'
import { PageLayout } from '@/ui/layout'
import { PageMeta } from '@/components/PageMeta'

type Phase = 'initializing' | 'ready' | 'error'

const MIN_PASSWORD_LENGTH = 8

async function establishRecoverySession(): Promise<void> {
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')

  if (code) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(code)
    if (error || !data.session) {
      throw new Error(error?.message ?? 'Failed to exchange authorization code.')
    }
    return
  }

  const hash = window.location.hash
  if (hash) {
    const hashParams = new URLSearchParams(hash.substring(1))
    const type = hashParams.get('type')
    if (type && type !== 'recovery') {
      throw new Error('This link is not valid for password reset.')
    }

    const accessToken = hashParams.get('access_token')
    const refreshToken = hashParams.get('refresh_token')
    const errorParam = hashParams.get('error')
    const errorDescription = hashParams.get('error_description')

    if (errorParam) {
      throw new Error(errorDescription ? decodeURIComponent(errorDescription) : errorParam)
    }

    if (accessToken && refreshToken) {
      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      })
      if (error || !data.session) {
        throw new Error(error?.message ?? 'Failed to create session from reset link.')
      }
      return
    }
  }

  const { data: existing } = await supabase.auth.getSession()
  if (existing.session) {
    return
  }

  throw new Error('Invalid or expired reset link. Request a new one from the sign in page.')
}

export default function ResetPassword() {
  const navigate = useNavigate()
  const [phase, setPhase] = useState<Phase>('initializing')
  const [errorMessage, setErrorMessage] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      try {
        await establishRecoverySession()
        if (cancelled) return
        window.history.replaceState(null, '', `${window.location.pathname}`)
        setPhase('ready')
      } catch (err) {
        if (cancelled) return
        setErrorMessage(err instanceof Error ? err.message : 'Something went wrong.')
        setPhase('error')
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (password.length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) {
        throw new Error(error.message)
      }
      await supabase.auth.signOut()
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      navigate('/login?reset=success', { replace: true })
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not update password.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageMeta title="Set new password" description="Choose a new password for your SitRepp.io account" />
      <PageLayout showHeader={false}>
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="w-full max-w-md">
            <Card>
              <CardHeader>
                <CardTitle>Set a new password</CardTitle>
                <CardDescription>
                  Choose a strong password you have not used elsewhere.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {phase === 'initializing' && (
                  <div className="flex flex-col items-center space-y-4 py-8 text-center">
                    <WaveLoader />
                    <p className="text-sm text-muted-foreground">Verifying your reset link…</p>
                  </div>
                )}

                {phase === 'error' && (
                  <div className="flex flex-col space-y-4 py-4">
                    <div
                      role="alert"
                      className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm flex items-start gap-2"
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{errorMessage}</span>
                    </div>
                    <Button type="button" variant="outline" onClick={() => navigate('/forgot-password', { replace: true })}>
                      Request a new link
                    </Button>
                    <button
                      type="button"
                      onClick={() => navigate('/login', { replace: true })}
                      className="text-sm text-primary underline underline-offset-2 text-left"
                    >
                      Back to sign in
                    </button>
                  </div>
                )}

                {phase === 'ready' && (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {formError && (
                      <div
                        role="alert"
                        className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm flex items-start gap-2"
                      >
                        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>{formError}</span>
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <label htmlFor="new-password" className="text-sm font-medium text-foreground">
                        New password
                      </label>
                      <div className="relative">
                        <Input
                          id="new-password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          autoComplete="new-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="h-11 rounded-xl pr-10"
                          minLength={MIN_PASSWORD_LENGTH}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="confirm-password" className="text-sm font-medium text-foreground">
                        Confirm password
                      </label>
                      <Input
                        id="confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="h-11 rounded-xl"
                        minLength={MIN_PASSWORD_LENGTH}
                      />
                    </div>

                    <Button type="submit" className="w-full h-11 rounded-xl font-semibold" disabled={submitting}>
                      {submitting ? 'Saving…' : 'Update password'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </PageLayout>
    </>
  )
}
